import { useEffect, useMemo, useState } from 'react';

import { DeleteIcon, DownloadIcon, InfoIcon, WarningIcon } from '@chakra-ui/icons';
import {
  AbsoluteCenter,
  Box,
  Button,
  ButtonGroup,
  Flex,
  IconButton,
  InputGroup,
  Select,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import {
  deleteCustomerForAssetTapeApi,
  downloadInstallmentsReportApi,
  getCustomersDealsOverviewApi,
  updateCascadeAssetTapeReportApi,
} from 'api/dealApi';
import { getFacilityFeesApi } from 'api/facilityFeeApi';
import DataTable from 'components/DataTable';
import { DestructiveDialog } from 'components/Dialog';
import { Currency } from 'components/Number';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import Card from 'components/card/Card';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { REVENUE_RECOGNITION_TYPE } from 'constants/dealConstants';
import { useNavigate } from 'react-router-dom';
import CashFlowFeeExposurePanel from './CashFlowFeeExposure';
import OperationsKpis, { ASSET_TAPE_VIEW } from './OperationsKpis';
import RevenueRecognitionSelect from './RevenueRecognitionSelect';

const columnHelper = createColumnHelper();

const getCurrencyCellValue = (info) => {
  const value = info.getValue();
  return value === null || value === undefined ? (
    'N/A'
  ) : (
    <Currency value={value} maximumFractionDigits={2} />
  );
};

const createActionsColumn = (onDeleteClick) =>
  columnHelper.display({
    id: 'actions',
    cell: (info) => {
      const customer = info.row.original;
      const hasOpenInstallments =
        (customer.amountDueOutstanding && customer.amountDueOutstanding > 0) ||
        (customer.principalDueOutstanding && customer.principalDueOutstanding > 0) ||
        (customer.feeDueOutstanding && customer.feeDueOutstanding > 0) ||
        (customer.currentExposure && customer.currentExposure > 0);

      return (
        <Tooltip
          label={
            hasOpenInstallments
              ? 'Cannot delete customer with open installments. Please close all installments first.'
              : 'This will hide the customer from Asset Tape page and exclude them from Asset Tape KPIs and reports. The customer will still appear in other pages.'
          }
          placement="top"
          hasArrow
        >
          <Flex align="center" gap={1}>
            <IconButton
              aria-label="Delete on Asset Tape"
              icon={<DeleteIcon color={hasOpenInstallments ? 'gray.400' : 'red'} />}
              bg="transparent"
              _hover={{ bg: 'transparent' }}
              isDisabled={hasOpenInstallments}
              onClick={(e) => {
                e.stopPropagation();
                if (!hasOpenInstallments) {
                  onDeleteClick(info.row.original);
                }
              }}
            />
            {hasOpenInstallments ? (
              <WarningIcon
                color="orange.500"
                boxSize={3}
                cursor="help"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <InfoIcon
                color="gray.500"
                boxSize={3}
                cursor="help"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </Flex>
        </Tooltip>
      );
    },
    header: 'Actions',
    enableSorting: false,
  });

export const createColumnsOld = (onDeleteClick) => [
  columnHelper.accessor('companyName', {
    cell: (info) => info.getValue(),
    header: 'Customer',
  }),
  columnHelper.accessor('totalPurchase', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Purchase Price',
  }),
  columnHelper.accessor('totalSold', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Amount Sold',
  }),
  columnHelper.accessor('amountDueOutstanding', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Amount Due Outstanding',
  }),
  columnHelper.accessor('principalDueOutstanding', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Principal Due Outstanding',
  }),
  columnHelper.accessor('feeDueOutstanding', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Fee Due Outstanding',
  }),
  columnHelper.accessor('principalRepaid', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Principal Repaid',
  }),
  columnHelper.accessor('feeRepaid', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Fee Repaid',
  }),
  createActionsColumn(onDeleteClick),
];

export const createColumnsNew = (onDeleteClick, facilityFeesByCustomer) => [
  columnHelper.accessor('companyName', {
    cell: (info) => info.getValue(),
    header: 'Customer',
  }),
  columnHelper.accessor('totalGrowthInvestment', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Growth Investment',
  }),
  columnHelper.accessor('currentReturns', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Current Returns',
  }),
  columnHelper.accessor('totalExpectedReturns', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Total Expected Returns',
  }),
  columnHelper.accessor('currentExposure', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Current Exposure',
  }),
  columnHelper.accessor('cumulativeFee', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Cumulative Fee',
  }),
  columnHelper.accessor('expectedFutureFee', {
    cell: (info) => getCurrencyCellValue(info),
    header: 'Expected Future Fee',
  }),
  columnHelper.accessor(
    (row) => {
      const fees = facilityFeesByCustomer[row.id] || [];
      return fees.reduce((sum, fee) => sum + (fee.amount || 0), 0);
    },
    {
      id: 'totalFacilityFee',
      cell: (info) => getCurrencyCellValue(info),
      header: 'Total Facility Fee',
    }
  ),
  columnHelper.accessor(
    (row) => {
      const fees = facilityFeesByCustomer[row.id] || [];
      return fees
        .filter((fee) => fee.status === 'paid')
        .reduce((sum, fee) => sum + (fee.amount || 0), 0);
    },
    {
      id: 'repaidFacilityFee',
      cell: (info) => getCurrencyCellValue(info),
      header: 'Repaid Facility Fee',
    }
  ),
  createActionsColumn(onDeleteClick),
];

const sumOptional = (acc, value) => {
  if (value === null || value === undefined) {
    return acc;
  }
  return (acc ?? 0) + value;
};

const computeKpisFromCustomers = (customers) => {
  return customers.reduce(
    (acc, c) => ({
      totalPurchase: acc.totalPurchase + (c.totalPurchase || 0),
      totalSold: acc.totalSold + (c.totalSold || 0),
      amountDueOutstanding: acc.amountDueOutstanding + (c.amountDueOutstanding || 0),
      principalDueOutstanding:
        acc.principalDueOutstanding + (c.principalDueOutstanding || 0),
      feeDueOutstanding: acc.feeDueOutstanding + (c.feeDueOutstanding || 0),
      principalRepaid: acc.principalRepaid + (c.principalRepaid || 0),
      feeRepaid: acc.feeRepaid + (c.feeRepaid || 0),
      totalGrowthInvestment: sumOptional(acc.totalGrowthInvestment, c.totalGrowthInvestment),
      currentReturns: sumOptional(acc.currentReturns, c.currentReturns),
      totalExpectedReturns: sumOptional(acc.totalExpectedReturns, c.totalExpectedReturns),
      currentExposure: sumOptional(acc.currentExposure, c.currentExposure),
      cumulativeFee: sumOptional(acc.cumulativeFee, c.cumulativeFee),
      expectedFutureFee: sumOptional(acc.expectedFutureFee, c.expectedFutureFee),
    }),
    {
      totalPurchase: 0,
      totalSold: 0,
      amountDueOutstanding: 0,
      principalDueOutstanding: 0,
      feeDueOutstanding: 0,
      principalRepaid: 0,
      feeRepaid: 0,
      totalGrowthInvestment: undefined,
      currentReturns: undefined,
      totalExpectedReturns: undefined,
      currentExposure: undefined,
      cumulativeFee: undefined,
      expectedFutureFee: undefined,
    }
  );
};

const OperationsOverview = () => {
  const [data, setData] = useState(null);
  const [facilityFeesByCustomer, setFacilityFeesByCustomer] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [tenantFilter, setTenantFilter] = useState('Treisar');
  const [loading, setLoading] = useState(true);
  const [revenueRecognition, setRevenueRecognition] = useState(
    REVENUE_RECOGNITION_TYPE.INTEREST_FIRST
  );
  const [assetTapeView, setAssetTapeView] = useState(ASSET_TAPE_VIEW.NEW);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const toast = useToast();
  const navigate = useNavigate();

  const fetchAllFacilityFees = async (customers) => {
    try {
      const results = await Promise.allSettled(
        customers.map(async (c) => {
          const fees = await getFacilityFeesApi(c.id);
          return { customerId: c.id, fees: fees || [] };
        })
      );
      const feeMap = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') feeMap[r.value.customerId] = r.value.fees;
      });
      setFacilityFeesByCustomer(feeMap);
    } catch {
      // non-critical
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getCustomersDealsOverviewApi({ revenueRecognition });
        setData(data);
        fetchAllFacilityFees(data.customers);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to get operation overview data',
          status: 'error',
        });
        throw error;
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [revenueRecognition]);

  const onDeleteClick = (customer) => {
    setDeleteError(null);
    setCustomerToDelete(customer);
  };

  const onDeleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      setLoading(true);
      await deleteCustomerForAssetTapeApi(customerToDelete.id);
      toast({
        title: 'Success',
        description: 'Customer deleted on Asset Tape successfully',
        status: 'success',
      });
      setCustomerToDelete(null);
      setDeleteError(null);

      const newData = await getCustomersDealsOverviewApi({ revenueRecognition });
      setData(newData);
      fetchAllFacilityFees(newData.customers);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete customer on Asset Tape';

      if (errorMessage.includes('open installments')) {
        setDeleteError('Cannot delete customer with open installments. Please close all installments first.');
      } else {
        toast({
          title: 'Error',
          description: errorMessage,
          status: 'error',
        });
        setCustomerToDelete(null);
        setDeleteError(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const filterCustomersByTenant = (_customers) => {
    if (tenantFilter === 'Combined') return _customers;
    return _customers.filter((c) => (c.tenant || 'Twelve') === tenantFilter);
  };

  const filterCustomers = (_customers) => {
    const tenantFiltered = filterCustomersByTenant(_customers);
    if (!searchValue) {
      return tenantFiltered;
    }

    return tenantFiltered.filter((customer) => {
      return (
        customer.companyName.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1 ||
        customer.id.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
      );
    });
  };

  const downloadInstallmentsReport = async () => {
    try {
      await downloadInstallmentsReportApi({
        fileName: 'installments_report.xlsx',
        tenant: tenantFilter,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download installments report',
        status: 'error',
      });
      throw error;
    }
  };

  const navigateToInstallment = (customerId) => {
    navigate(`/operations/${customerId}/installment`);
  };

  const columns = useMemo(() => {
    if (assetTapeView === ASSET_TAPE_VIEW.OLD) {
      return createColumnsOld(onDeleteClick);
    }
    return createColumnsNew(onDeleteClick, facilityFeesByCustomer);
  }, [assetTapeView, facilityFeesByCustomer]);

  if (loading && !data) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  const tenantFilteredCustomers = filterCustomersByTenant(data?.customers || []);
  const displayedKpis = computeKpisFromCustomers(tenantFilteredCustomers);
  const facilityFeeKpis = tenantFilteredCustomers.reduce(
    (acc, c) => {
      const fees = facilityFeesByCustomer[c.id] || [];
      return {
        totalFacilityFee: acc.totalFacilityFee + fees.reduce((s, f) => s + (f.amount || 0), 0),
        repaidFacilityFee:
          acc.repaidFacilityFee +
          fees.filter((f) => f.status === 'paid').reduce((s, f) => s + (f.amount || 0), 0),
      };
    },
    { totalFacilityFee: 0, repaidFacilityFee: 0 }
  );

  return (
    <Box maxW={'container.xl'}>
      <OverlaySpinnerWrapper show={loading}>
        <Flex mb={4} align={'center'} gap={3} justify={'space-between'} wrap={'wrap'}>
          <Flex align={'center'} gap={3}>
            <Text fontWeight={500} fontSize={'sm'} whiteSpace={'nowrap'}>
              Tenant:
            </Text>
            <Select w={180} value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)}>
              <option value="Treisar">Treisar</option>
              <option value="Twelve">Twelve</option>
              <option value="Combined">Combined</option>
            </Select>
          </Flex>
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              onClick={() => setAssetTapeView(ASSET_TAPE_VIEW.OLD)}
              colorScheme={assetTapeView === ASSET_TAPE_VIEW.OLD ? 'brand' : undefined}
              variant={assetTapeView === ASSET_TAPE_VIEW.OLD ? 'solid' : 'outline'}
            >
              Old view
            </Button>
            <Button
              onClick={() => setAssetTapeView(ASSET_TAPE_VIEW.NEW)}
              colorScheme={assetTapeView === ASSET_TAPE_VIEW.NEW ? 'brand' : undefined}
              variant={assetTapeView === ASSET_TAPE_VIEW.NEW ? 'solid' : 'outline'}
            >
              New view
            </Button>
          </ButtonGroup>
        </Flex>
        <Flex justifyContent={'space-between'} align={'strech'}>
          <OperationsKpis view={assetTapeView} {...displayedKpis} {...facilityFeeKpis} />
        </Flex>
        <Flex justify={'end'} mt={5}>
          <Button variant={'outline'} leftIcon={<DownloadIcon />} onClick={downloadInstallmentsReport}>
            Download Installments Report
          </Button>
        </Flex>
        <Card mt={5}>
          <InputGroup mb={6} justifyContent={'space-between'}>
            {' '}
            <SearchBar
              h="44px"
              onChange={(event) => setSearchValue(event.target.value)}
              w={{ lg: '390px' }}
              placeholder="Search by Company Name"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const customers = filterCustomers(data.customers);
                  if (customers.length > 0) {
                    navigateToInstallment(customers[0].id);
                  }
                }
              }}
            />
            <RevenueRecognitionSelect value={revenueRecognition} onChange={setRevenueRecognition} />
          </InputGroup>
          <Box minH={300} mx={-5}>
            <DataTable
              columns={columns}
              data={filterCustomers(data?.customers || [])}
              hasPagination={true}
              defaultSorting={[
                {
                  id: assetTapeView === ASSET_TAPE_VIEW.NEW ? 'totalGrowthInvestment' : 'totalSold',
                  desc: true,
                },
              ]}
              onRowClick={(row) => {
                navigateToInstallment(row.id);
              }}
              rowHeight={'100px'}
            />
          </Box>
        </Card>
        <CashFlowFeeExposurePanel tenantFilter={tenantFilter} />
        <DestructiveDialog
          header={deleteError ? 'Cannot Delete Customer on Asset Tape' : 'Delete Customer on Asset Tape'}
          body={
            deleteError
              ? deleteError
              : customerToDelete
                ? `Are you sure you want to delete ${customerToDelete.companyName} on Asset Tape? This will hide the customer from Asset Tape page and exclude them from Asset Tape KPIs and reports. The customer will still appear in other pages.`
                : null
          }
          deleteButtonText={deleteError ? 'OK' : 'Delete on Asset Tape'}
          show={!!customerToDelete}
          onDelete={() => {
            if (deleteError) {
              setCustomerToDelete(null);
              setDeleteError(null);
            } else {
              onDeleteCustomer();
            }
          }}
          onDialogClose={() => {
            setCustomerToDelete(null);
            setDeleteError(null);
          }}
        />
      </OverlaySpinnerWrapper>
    </Box>
  );
};

OperationsOverview.propTypes = {};

export default OperationsOverview;
